import React, { useEffect, useState, Fragment, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../Panel.module.css';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Divider, Typography, Button, TextField, MenuItem } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { WorkflowWidget } from '../helpers/panel-functions';
import { FieldsRender } from '../helpers/panel-functions';
import {
  elementUpdated,
  selectElementById,
  selectFilterMode,
  saveWorkflow
} from '../../features/workflow/workflowSlice';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e0e0e0'}`,
    borderRadius: 4,
    padding: theme.spacing(2),
    width: '90%',
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
  },
  selectField: {
    width: '97.5%',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(1)
  },
  textField: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(1),
    textTransform: 'none'
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`,
    backgroundColor: theme.palette.primary.main,
    fontWeight: 500
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
}));

function FilterPanel({ selectedEdge, onPaneClick, parentTree, inlineLayout = false }) {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();

  // Retrieve the edge from Redux
  const edge = useSelector(selectElementById(selectedEdge.id));
  const filterMode = useSelector(selectFilterMode); // 'filter' or 'delay'
  const delayOnly = filterMode?.mode === 'delay';
  // Local state for basic edge info
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [label, setLabel] = useState('');
  const [icon, setIcon] = useState('');
  const [fields, setFields] = useState([]);
  const [testData, setTestData] = useState(null);
  
  // Local state for delay value (for delay-only mode)
  const [delayValue, setDelayValue] = useState('');
  const [delayError, setDelayError] = useState('');
  // Ref to store WorkflowWidget instance to get current value
  const workflowWidgetRef = useRef(null);
  // Ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);
  // Ref to track the previous value to prevent unnecessary updates
  const previousValueRef = useRef(null);
  // Ref to force remount when delay value loads
  const delayWidgetKeyRef = useRef(0);
  
  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Local state for filter rules.
  // "andFilters" is an array of rule objects; "orGroups" is an array of arrays of rule objects.
  const [andFilters, setAndFilters] = useState([]);
  const [orGroups, setOrGroups] = useState([]);

  // When the edge from Redux changes (or on mount), load its data into local state.
  useEffect(() => {
    if (edge && edge.data) {
      setName(edge.data.name || '');
      setType(edge.data.type || '');
      setLabel(edge.data.label || '');
      setIcon(edge.data.icon || '');
      setFields(edge.data.fields || []);
      setTestData(edge.data.testData || null);
      
      // Set delay value from fields[2] if it exists (for delay-only mode)
      const initialDelay = edge.data.fields?.[2]?.value || '';
      // Reset previousValueRef to undefined before setting new value to ensure it displays
      previousValueRef.current = undefined;
      // Force remount of WorkflowWidget when delay value loads
      delayWidgetKeyRef.current += 1;
      setDelayValue(initialDelay);
      // Validate initial value
      if (initialDelay !== '') {
        const isValid = /^\d+$/.test(String(initialDelay)) && parseInt(initialDelay, 10) > 0;
        setDelayError(isValid ? '' : 'Enter a positive integer (seconds)');
      } else {
        setDelayError('');
      }

      // Parse the filter rule from fields[1] if it exists.
      // We expect fields[1].value to be an array where the first element is an array of AND rules
      // and subsequent elements (if any) are OR groups.
      const ruleValue = edge.data.fields?.[1]?.value;
      if (
        Array.isArray(ruleValue) &&
        ruleValue.length > 0 &&
        Array.isArray(ruleValue[0]) &&
        ruleValue[0].length > 0
      ) {
        setAndFilters(ruleValue[0]);
        setOrGroups(ruleValue.slice(1));
      } else {
        // Otherwise, initialize with a default AND rule
        setAndFilters([{ lhs: '', conditionType: '', rhs: '' }]);
        setOrGroups([]);
      }
    }
  }, [edge]);

  // Helper function to auto generate a filter name
  function extractFieldName(lhs) {
    const regex = /\$\{([^}]+)\}/;
    const match = lhs.match(regex);
    if (match) {
      const parts = match[1].split('.');
      return parts[parts.length - 1]; // Return the last part, e.g. "date"
    }
    return lhs;
  }
  const generateFilterName = () => {
    

    let parts = [];

    // Build string for AND filters
    if (andFilters.length > 0) {
      const andPart = andFilters
        .map(filter => {
          const lhsDisplay = filter.lhs && filter.lhs.includes('${')
          ? extractFieldName(filter.lhs)
          : filter.lhs || "";
          return `${lhsDisplay}`.trim();
        })
        .filter(s => s !== "")
        .join(" AND ");
      if (andPart) {
        parts.push(andPart);
      }
    }

    // Build string for each OR group
    if (orGroups.length > 0) {
      orGroups.forEach(group => {
        const groupPart = group
          .map(filter => {
            const lhsDisplay = filter.lhs && filter.lhs.includes('${')
          ? extractFieldName(filter.lhs)
          : filter.lhs || "";
            return `${lhsDisplay}`.trim();
          })
          .filter(s => s !== "")
          .join(" AND ");
        if (groupPart) {
          parts.push(`(${groupPart})`);
        }
      });
    }

    if (parts.length > 0) {
      return "Filter: " + parts.join(" OR ");
    }
    return "Filter";
  };

  // Handlers for AND filters
  const handleAddAndFilter = () => {
    setAndFilters([...andFilters, { lhs: '', conditionType: '', rhs: '' }]);
  };

  // Handler for adding an OR group
  const handleAddOrGroup = () => {
    setOrGroups([...orGroups, [{ lhs: '', conditionType: '', rhs: '' }]]);
  };

  // Handler for adding another filter to an existing OR group
  const handleAddFilterToOrGroup = (groupIndex) => {
    const updatedOrGroups = orGroups.map((orGroup, i) =>
      i === groupIndex ? [...orGroup, { lhs: '', conditionType: '', rhs: '' }] : orGroup
    );
    setOrGroups(updatedOrGroups);
  };

  // Handler for filter changes; works for both "and" and "or" groups.
  // For "and", group is the string 'and'; for OR groups, group is the numeric index.
  const handleFilterChange = (group, index, key, value) => {
    if (group === 'and') {
      const updatedAndFilters = andFilters.map((filter, i) =>
        i === index ? { ...filter, [key]: value } : filter
      );
      setAndFilters(updatedAndFilters);
    } else {
      const updatedOrGroups = orGroups.map((orGroup, gIndex) =>
        gIndex === group
          ? orGroup.map((filter, i) =>
              i === index ? { ...filter, [key]: value } : filter
            )
          : orGroup
      );
      setOrGroups(updatedOrGroups);
    }
  };

  // Handler to delete a filter rule from AND or OR groups
  const handleDeleteFilter = (group, index) => {
    if (group === 'and') {
      const updatedAndFilters = andFilters.filter((_, i) => i !== index);
      setAndFilters(updatedAndFilters);
    } else {
      const updatedOrGroups = orGroups
        .map((orGroup, gIndex) =>
          gIndex === group ? orGroup.filter((_, i) => i !== index) : orGroup
        )
        .filter((orGroup) => orGroup.length > 0);
      setOrGroups(updatedOrGroups);
    }
  };

  // Save button handler – combine AND and OR rules into the second field's value,
  // auto-generate a name, and dispatch update.
  const handleSave = () => {
    if (delayOnly) {
      // For delay-only, update only fields[2] (the delay field)
      // Get the current value from fields[2] or delayValue, ensuring we have the latest
      // Since WorkflowWidget uses debounced onChange, we'll use fields[2].value which
      // should be updated by our onChange handler, or fall back to delayValue
      const currentDelayValue = fields[2]?.value ?? delayValue ?? '';
      // Final validation before save
      const isValid = /^\d+$/.test(String(currentDelayValue)) && parseInt(currentDelayValue, 10) > 0;
      if (!isValid) {
        setDelayError('Enter a positive integer (seconds)');
        return;
      }
      const updatedDelayField = {
        ...fields[2],
        value: currentDelayValue  // Use the current value from fields or state
      };
      const newFields = [fields[0], fields[1], updatedDelayField];
      const updatedData = { name, type, label, icon, fields: newFields, testData };
      dispatch(elementUpdated(selectedEdge.id, updatedData, null));
      // Persist immediately so delay shows up when reopening and in backend
      dispatch(saveWorkflow(1));
    } else {
    const combinedRules = [andFilters, ...orGroups];
    const updatedRuleField = {
      ...fields[1],
      value: combinedRules
    };

    // Auto-generate filter name
    const autoName = generateFilterName();
    // Reconstruct the complete fields array (assuming fields[0] and fields[2] remain unchanged)
    const newFields = [fields[0], updatedRuleField, fields[2]];

    const updatedData = {
      name: autoName === 'Filter' ? null : autoName,
      type,
      label,
      icon,
      fields: newFields,
      testData
    };
    dispatch(elementUpdated(selectedEdge.id, updatedData, null));
    // Persist filter rules as well
    dispatch(saveWorkflow(1));
  }
    onPaneClick();
  };
// If delayOnly is true, render only the delay field (fields[2])
const panelClassName = inlineLayout ? undefined : styles.Panel;
const panelStyle = { paddingBottom: '60px', cursor: 'default', ...(inlineLayout ? { position: 'relative', width: '100%', height: '100%', minHeight: 0, overflow: 'auto' } : {}) };

if (delayOnly) {
  return (
    <div className={panelClassName} style={panelStyle}>
      <Box className={classes.container} style={{ marginTop: '80px' }}>
        <div id="delay-field-wrapper" style={{ position: 'relative' }}>
          <WorkflowWidget
            key={`delay-${selectedEdge?.id || ''}-${delayWidgetKeyRef.current}`}
            value={delayValue}
            onChange={(val) => {
              // Only update state if component is still mounted
              if (!isMountedRef.current) return;
              
              try {
                const newValue = val || '';
                
                // Prevent infinite loop: only update if value actually changed
                // Allow updates if previousValueRef is undefined (initial load)
                if (previousValueRef.current !== undefined && previousValueRef.current === newValue) {
                  return;
                }
                previousValueRef.current = newValue;
                
                // Update the delay value state immediately
                setDelayValue(newValue);
                // Validate on change – must be positive integer
                if (newValue === '') {
                  setDelayError('');
                } else {
                  const ok = /^\d+$/.test(String(newValue)) && parseInt(newValue, 10) > 0;
                  setDelayError(ok ? '' : 'Enter a positive integer (seconds)');
                }
                // Also update the fields array to keep it in sync
                const updatedFields = [...fields];
                updatedFields[2] = {
                  ...updatedFields[2],
                  value: newValue
                };
                setFields(updatedFields);
              } catch (error) {
                // Silently ignore errors during unmount or hot reload
                // Don't log to console to prevent error spam
              }
            }}
            // Use the delay field's schema if it exists; otherwise provide a default.
            schema={fields[2]?.schema || { type: 'string', title: 'Delay Time (seconds)' }}
          />
          {delayError ? (
            <Typography style={{ color: theme.palette.error.main, fontSize: '12px', marginTop: '6px' }}>{delayError}</Typography>
          ) : null}
        </div>
      </Box>
      <Box mt={2}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => {
            // Since WorkflowWidget uses debounced onChange that only fires on unmount,
            // we need to get the current value directly from the CodeMirror editor before saving
            const wrapper = document.querySelector('#delay-field-wrapper');
            if (wrapper) {
              const cmElement = wrapper.querySelector('.CodeMirror');
              if (cmElement && cmElement.CodeMirror) {
                const currentValue = cmElement.CodeMirror.getValue() || '';
                const ok = /^\d+$/.test(String(currentValue)) && parseInt(currentValue, 10) > 0;
                if (!ok) {
                  setDelayError('Enter a positive integer (seconds)');
                  return;
                }
                // Update state with current editor value before saving
                setDelayValue(currentValue);
                const updatedFields = [...fields];
                updatedFields[2] = {
                  ...updatedFields[2],
                  value: currentValue
                };
                setFields(updatedFields);
                // Now save with the current value
                const updatedDelayField = {
                  ...fields[2],
                  value: currentValue
                };
                const newFields = [fields[0], fields[1], updatedDelayField];
                const updatedData = { name, type, label, icon, fields: newFields, testData };
                dispatch(elementUpdated(selectedEdge.id, updatedData, null));
                dispatch(saveWorkflow(1));
                onPaneClick();
                return;
              }
            }
            // Fallback to normal handleSave if we can't get the editor value
            handleSave();
          }} 
          className={classes.button}
          disabled={Boolean(delayError) || (delayValue !== '' && (!/^\d+$/.test(String(delayValue)) || parseInt(delayValue, 10) <= 0))}
        >
          Save delay
        </Button>
      </Box>
    </div>
  );
}

  return (
    <>
      <div className={panelClassName} style={panelStyle}>
        <Box className={classes.container}>
          {/* Render AND filters */}
          {andFilters.map((filter, index) => (
            <Box key={index} mb={2} className={classes.container}>
              <Box className={classes.filterHeader}>
                <Typography variant="subtitle2" style={{ color: theme.palette.text.primary }}>
                  {index === 0 ? "Only continue if" : "And"}
                </Typography>
                <Box display="flex" alignItems="center">
                  <Button
                    onClick={() => handleDeleteFilter('and', index)}
                    size="small"
                    variant="text"
                    style={{ textTransform: 'none' }}
                  >
                    <DeleteIcon fontSize="small" color="primary" />
                    <Typography color="primary" variant="body2">
                      Remove rule
                    </Typography>
                  </Button>
                </Box>
              </Box>
              <WorkflowWidget
                value={filter.lhs}
                onChange={(val) => handleFilterChange('and', index, 'lhs', val)}
                schema={{
                  type: 'string',
                  title: 'Choose Field...'
                }}
              />
              <TextField
                select
                label="Choose condition..."
                value={filter.conditionType}
                onChange={(e) => handleFilterChange('and', index, 'conditionType', e.target.value)}
                variant="outlined"
                className={classes.selectField}
              >
                <MenuItem value="stringEqual">(Text/Number) Equals</MenuItem>
                <MenuItem value="stringUnequal">(Text/Number) Does not equal</MenuItem>
                <MenuItem value="listContains">(Text) Contains</MenuItem>
                <MenuItem value="listNotContain">(Text) Does not contain</MenuItem>
                <MenuItem value="greaterThan">(Number) Greater than</MenuItem>
                <MenuItem value="lessThan">(Number) Less than</MenuItem>
                <MenuItem value="isTrue">(Boolean) Is true</MenuItem>
                <MenuItem value="isFalse">(Boolean) Is false</MenuItem>
                <MenuItem value="valueExists">Exists</MenuItem>
                <MenuItem value="valueNotExists">Does not exist</MenuItem>
              </TextField>
              <WorkflowWidget
                value={filter.rhs}
                onChange={(val) => handleFilterChange('and', index, 'rhs', val)}
                schema={{
                  type: 'string',
                  title: 'Enter/select value...'
                }}
              />
            </Box>
          ))}
          <Button variant="outlined" color="primary" onClick={handleAddAndFilter} className={classes.button}>
            + And
          </Button>
        </Box>

        <Divider className={classes.divider} light={true} variant="fullWidth" />

        {/* Render OR groups */}
        {orGroups.map((orGroup, groupIndex) => (
          <Box
            key={groupIndex}
            className={classes.container}
            style={{ 
              backgroundColor: groupIndex % 2 === 0 
                ? (theme.palette.type === 'dark' ? theme.palette.action.hover : '#f9f9f9')
                : theme.palette.background.paper
            }}
          >
            {orGroup.map((filter, index) => (
              <Box key={index} mb={2} className={classes.container}>
                <Box className={classes.filterHeader}>
                  <Typography variant="subtitle2" style={{ color: theme.palette.text.primary }}>
                    {index === 0 ? "Or continue if" : "And"}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Button
                      onClick={() => handleDeleteFilter(groupIndex, index)}
                      size="small"
                      variant="text"
                      style={{ textTransform: 'none' }}
                    >
                      <DeleteIcon fontSize="small" color="primary" />
                      <Typography color="primary" variant="body2">
                        Remove rule
                      </Typography>
                    </Button>
                  </Box>
                </Box>
                <WorkflowWidget
                  value={filter.lhs}
                  onChange={(val) => handleFilterChange(groupIndex, index, 'lhs', val)}
                  schema={{ type: 'string', title: 'Choose Field...' }}
                />
                <TextField
                  select
                  label="Choose condition..."
                  value={filter.conditionType}
                  onChange={(e) => handleFilterChange(groupIndex, index, 'conditionType', e.target.value)}
                  variant="outlined"
                  className={classes.selectField}
                >
                  <MenuItem value="stringEqual">(Text/Number) Equals</MenuItem>
                  <MenuItem value="stringUnequal">(Text/Number) Does not equal</MenuItem>
                  <MenuItem value="listContains">(Text) Contains</MenuItem>
                  <MenuItem value="listNotContain">(Text) Does not contain</MenuItem>
                  <MenuItem value="greaterThan">(Number) Greater than</MenuItem>
                  <MenuItem value="lessThan">(Number) Less than</MenuItem>
                  <MenuItem value="isTrue">(Boolean) Is true</MenuItem>
                  <MenuItem value="isFalse">(Boolean) Is false</MenuItem>
                  <MenuItem value="valueExists">Exists</MenuItem>
                  <MenuItem value="valueNotExists">Does not exist</MenuItem>
                </TextField>
                <WorkflowWidget
                  value={filter.rhs}
                  onChange={(val) => handleFilterChange(groupIndex, index, 'rhs', val)}
                  schema={{ type: 'string', title: 'Enter/select value...' }}
                />
              </Box>
            ))}
            <Button variant="outlined" color="primary" onClick={() => handleAddFilterToOrGroup(groupIndex)} className={classes.button}>
              + And
            </Button>
          </Box>
        ))}
        <Button variant="outlined" color="primary" onClick={handleAddOrGroup} className={classes.button}>
          + Add "Or" rule group
        </Button>

        {/* Save button */}
        <Box mt={2}>
          <Button variant="contained" color="primary" onClick={handleSave} className={classes.button}>
            Save filter
          </Button>
        </Box>
      </div>
    </>
  );
}

export default FilterPanel;
